import { useState } from 'react'
import Bildfehler from '../komponenten/Bildfehler'
import Ladehinweis from '../komponenten/Ladehinweis'
import Stapel from '../komponenten/Stapel'
import { useBild } from '../lib/bild'
import { datum, seit, useLaden } from '../lib/laden'
import { ApiFehler } from '../lib/api'
import {
  ABLEHNUNGSGRUND_TEXT,
  GRUENDE_PROFILBILD,
  GRUND_TEXT,
  holeProfilmeldungen,
  profilbildSetzen,
  profilmeldungEntscheiden,
  selbstbeschreibungSetzen,
  type Ablehnungsgrund,
  type Profilmeldung,
  type Sichtbarkeit,
} from '../lib/warteschlangen'

/**
 * Die fünfte Warteschlange: Meldungen an Profilen.
 *
 * Sie zeigt **einen Menschen**, keinen Ort und kein Bild an einem Ort — als
 * einzige. Und sie tut von selbst nichts: Eine angenommene Meldung verbirgt
 * weder Text noch Bild. Die drei Handlungen stehen nebeneinander, und wer
 * entscheidet, wählt.
 */
export default function Profilmeldungen() {
  const [zustand, neuLaden] = useLaden(holeProfilmeldungen)

  if (zustand.art !== 'da') return <Ladehinweis zustand={zustand} />

  return (
    <Stapel<Profilmeldung>
      titel="Profilmeldungen"
      eintraege={zustand.wert.eintraege}
      gesamt={zustand.wert.gesamt}
      schluessel={(m) => m.id}
      neuLaden={neuLaden}
      // Kein Ablehnungsgrund an der Meldung selbst. Wird daraufhin das
      // Profilbild verborgen, trägt jene Entscheidung den Grund — sie ist
      // eine eigene, und nur sie hat einen Betroffenen.
      gruende={[]}
      leerText="Keine Meldung an einem Profil wartet auf eine Entscheidung."
      freigebenText="Meldung trifft zu"
      ablehnenText="Meldung trifft nicht zu"
      zeile={(m) => (
        <>
          <span className="stapel-zeile-haupt">
            {m.gemeldet_name ?? `Anonym · ${m.gemeldet_uid.slice(0, 6)}`}
          </span>
          <span className="leise">
            {GRUND_TEXT[m.reason] ?? m.reason}
            {m.meldungen_gesamt > 1 && ` · ${m.meldungen_gesamt}×`}
          </span>
        </>
      )}
      inhalt={(m) => <Ansicht m={m} neuLaden={neuLaden} />}
      aktion={{
        freigeben: (m) => profilmeldungEntscheiden(m.id, 'approved'),
        ablehnen: (m) => profilmeldungEntscheiden(m.id, 'rejected'),
        bericht: (_m, _e, art) =>
          art === 'frei'
            ? 'Meldung trifft zu — am Profil wurde dadurch nichts verändert.'
            : 'Meldung trifft nicht zu.',
      }}
    />
  )
}

function Ansicht({ m, neuLaden }: { m: Profilmeldung; neuLaden: () => void }) {
  const name = m.gemeldet_name ?? `Anonym · ${m.gemeldet_uid.slice(0, 6)}`

  return (
    <div className="ansicht">
      <div className="karte">
        <p className="marke">Profil gemeldet</p>
        <h2>{GRUND_TEXT[m.reason] ?? m.reason}</h2>
        {m.note && <p className="bewertung-text">{m.note}</p>}
        <p className="leise ansicht-zeile">
          Gemeldet: {datum(m.created_at)} · von {m.melder}
        </p>
      </div>

      {/* Die wichtigste Zahl der Ansicht: Eine Meldung sagt wenig, drei viel. */}
      {m.meldungen_gesamt > 1 && (
        <p className="meldung meldung-warnung">
          Diese Person wurde <strong>{m.meldungen_gesamt}-mal</strong> gemeldet,
          diese Meldung eingeschlossen.
        </p>
      )}

      <div className="karte">
        <div className="mensch">
          <KleinesProfilbild m={m} />
          <div className="mensch-text">
            <p className="mensch-name">{name}</p>
            <p className="gedaempft mensch-zahlen">
              <span className="zahl">{m.gemeldet_punkte}</span> Punkte · dabei
              seit {datum(m.gemeldet_seit)}
              {m.zuletzt_gesehen && ` · zuletzt ${seit(m.zuletzt_gesehen)}`}
            </p>
            <p className={m.abgelehnte_beitraege > 0 ? 'mensch-abgelehnt' : 'leise'}>
              <span className="zahl">{m.abgelehnte_beitraege}</span>{' '}
              {m.abgelehnte_beitraege === 1 ? 'Beitrag' : 'Beiträge'} bisher abgelehnt
            </p>
          </div>
        </div>

        {/* `profile_hidden` sagt etwas über die App, nicht über diese Ansicht:
            Für die Moderation bleibt die Person vollständig sichtbar. */}
        {m.profile_hidden && (
          <p className="leise ansicht-zeile">
            Das Profil ist in der App verborgen. Hier steht es trotzdem
            vollständig — sonst könnte man nicht entscheiden.
          </p>
        )}
      </div>

      <Selbstbeschreibung m={m} neuLaden={neuLaden} />
      <ProfilbildKarte m={m} neuLaden={neuLaden} />

      <p className="meldung meldung-hinweis">
        Die drei Handlungen sind getrennt: <strong>Verbergen und Zeigen wirken
        sofort</strong>, unabhängig davon, wie du die Meldung unten
        entscheidest. Eine angenommene Meldung verbirgt von selbst nichts.
      </p>
    </div>
  )
}

/**
 * Ein Knopf, der sofort wirkt — und danach sagt, was jetzt gilt.
 *
 * Stehen `gruende` zur Wahl, fragt das **Verbergen** vorher nach einem: Es
 * entsteht dabei eine Zeile in `core_decision`, und die geht den Menschen
 * etwas an. Das Wiederzeigen fragt nie — ein wiederhergestellter Beitrag hat
 * keinen Mangel, und die Datenbank ließe dort auch keinen Grund zu.
 */
function Umschalter({
  aktuell,
  umschalten,
  verbergenText,
  zeigenText,
  gruende,
}: {
  aktuell: Sichtbarkeit | string
  umschalten: (status: Sichtbarkeit, grundCode: Ablehnungsgrund | null) => Promise<void>
  verbergenText: string
  zeigenText: string
  /** Fehlt die Liste, wirkt der Knopf ohne Rückfrage. */
  gruende?: Ablehnungsgrund[]
}) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [fragt, setFragt] = useState(false)
  const [code, setCode] = useState<Ablehnungsgrund | null>(null)
  const verborgen = aktuell === 'hidden'
  const fragtNach = Boolean(gruende?.length) && !verborgen

  async function ausfuehren(status: Sichtbarkeit, grundCode: Ablehnungsgrund | null) {
    setLaeuft(true)
    setFehler(null)
    try {
      await umschalten(status, grundCode)
      setFragt(false)
      setCode(null)
    } catch (f) {
      setFehler(
        f instanceof ApiFehler || f instanceof Error ? f.message : 'Unbekannter Fehler',
      )
    } finally {
      setLaeuft(false)
    }
  }

  function klick() {
    if (verborgen) return void ausfuehren('published', null)
    if (fragtNach) return setFragt(true)
    void ausfuehren('hidden', null)
  }

  return (
    <>
      {fehler && (
        <p className="meldung meldung-fehler" role="alert">
          {fehler}
        </p>
      )}

      {fragt && gruende && (
        <div className="begruendung begruendung-schmal">
          <h4>Warum verbergen?</h4>
          <p className="leise">
            Diese Auswahl bekommt der Mensch zu lesen — die App macht daraus
            einen Satz in seiner Sprache. Sie wird als Entscheidung
            festgehalten.
          </p>
          <div className="gruende" role="radiogroup" aria-label="Grund fürs Verbergen">
            {gruende.map((g) => (
              <label key={g} className={g === code ? 'grund aktiv' : 'grund'}>
                <input
                  type="radio"
                  name="profilbild-grund"
                  checked={g === code}
                  onChange={() => setCode(g)}
                />
                <span>{ABLEHNUNGSGRUND_TEXT[g]}</span>
              </label>
            ))}
          </div>
          <div className="knopf-reihe">
            <button
              className="knopf knopf-ab"
              type="button"
              disabled={!code || laeuft}
              onClick={() => code && void ausfuehren('hidden', code)}
            >
              {laeuft ? 'einen Moment …' : verbergenText}
            </button>
            <button
              className="knopf knopf-still"
              type="button"
              onClick={() => {
                setFragt(false)
                setCode(null)
              }}
            >
              Abbrechen
            </button>
            {!code && <p className="leise">Bitte einen Grund wählen.</p>}
          </div>
        </div>
      )}

      {!fragt && (
        <div className="knopf-reihe">
          <button
            className={verborgen ? 'knopf knopf-frei' : 'knopf knopf-ab'}
            type="button"
            disabled={laeuft}
            onClick={klick}
          >
            {laeuft ? 'einen Moment …' : verborgen ? zeigenText : verbergenText}
          </button>
        </div>
      )}
    </>
  )
}

function Selbstbeschreibung({
  m,
  neuLaden,
}: {
  m: Profilmeldung
  neuLaden: () => void
}) {
  const verborgen = m.textstatus === 'hidden'
  const text = m.gemeldet_text_roh

  return (
    <div className="karte">
      <h3>Selbstbeschreibung</h3>

      {text ? (
        <p className={verborgen ? 'bewertung-text leise' : 'bewertung-text'}>{text}</p>
      ) : (
        <p className="gedaempft">Diese Person hat keine Selbstbeschreibung.</p>
      )}

      {/* Ohne Text sagt der Status nichts — dann ist auch nichts verborgen. */}
      {verborgen && text && (
        <p className="meldung meldung-warnung">
          Verborgen — in der App steht sie nicht mehr. Hier bleibt sie lesbar,
          damit man das zurücknehmen kann.
        </p>
      )}

      {text && (
        <p className="leise gesehen-zeile">
          Verbergen heißt: unsichtbar, nicht verändert. Der Text bleibt der des
          Menschen — die Moderation entscheidet über die Sichtbarkeit, nicht
          über den Inhalt.
        </p>
      )}

      {/* Anders als beim Profilbild entsteht hier keine Entscheidungszeile
          und wird deshalb auch kein Grund erhoben: `core_decision.target_id`
          ist `uuid NOT NULL`, ein Konto hat aber keine UUID. Siehe die
          ausführliche Begründung in `daten/profilmeldungen.ts`. */}
      {text && (
        <p className="leise gesehen-zeile">
          Diese Handlung wird nicht als Entscheidung festgehalten — anders als
          beim Profilbild. Ein Konto lässt sich in{' '}
          <span className="zahl">core_decision</span> noch nicht adressieren:
          Die Spalte <span className="zahl">target_id</span> ist eine UUID, ein
          Konto hat nur eine <span className="zahl">firebase_uid</span>.
        </p>
      )}

      {text && (
        <Umschalter
          aktuell={m.textstatus}
          verbergenText="Selbstbeschreibung verbergen"
          zeigenText="Selbstbeschreibung wieder zeigen"
          umschalten={async (status) => {
            await selbstbeschreibungSetzen(m.gemeldet_uid, status)
            neuLaden()
          }}
        />
      )}
    </div>
  )
}

function ProfilbildKarte({ m, neuLaden }: { m: Profilmeldung; neuLaden: () => void }) {
  const verborgen = m.gemeldet_bild_status === 'hidden'

  return (
    <div className="karte bild-karte">
      <h3>Profilbild</h3>

      {m.gemeldet_bild_id ? (
        <>
          <GrossesProfilbild m={m} />
          {verborgen && (
            <p className="meldung meldung-warnung">
              Verborgen — in der App ist es nicht mehr zu sehen. Die Punkte
              bleiben, solange das so ist.
            </p>
          )}
          <p className="leise gesehen-zeile">
            Verbergen setzt <span className="zahl">status = 'hidden'</span>, nicht{' '}
            <span className="zahl">rejected</span>: eine
            Moderationsentscheidung, kein abgelehnter Beitrag. Die Datei bleibt
            liegen, die Punkte bleiben stehen. Im Protokoll heißt derselbe
            Vorgang <span className="zahl">reverted</span> — war öffentlich,
            ist es nicht mehr.
          </p>
          <Umschalter
            aktuell={m.gemeldet_bild_status ?? 'published'}
            verbergenText="Profilbild verbergen"
            zeigenText="Profilbild wieder zeigen"
            gruende={GRUENDE_PROFILBILD}
            umschalten={async (status, grundCode) => {
              await profilbildSetzen(m.gemeldet_bild_id as string, status, grundCode)
              neuLaden()
            }}
          />
        </>
      ) : (
        <p className="gedaempft">Diese Person hat kein Profilbild.</p>
      )}
    </div>
  )
}

/** Der Kreis neben dem Namen — dafür reicht die 400er-Variante. */
function KleinesProfilbild({ m }: { m: Profilmeldung }) {
  const bild = useBild(m.gemeldet_bild_klein ?? m.gemeldet_bild, m.gemeldet_bild)
  if (bild.art !== 'da') {
    return <div className="mensch-bild mensch-bild-leer" aria-hidden="true" />
  }
  return <img className="mensch-bild" src={bild.adresse} alt="" />
}

function GrossesProfilbild({ m }: { m: Profilmeldung }) {
  // Das Original, wie im Entscheidungsbildschirm der Fotos: Über den Inhalt
  // urteilt man nicht an einem Vorschaubild.
  const bild = useBild(m.gemeldet_bild)

  if (bild.art === 'laedt') return <p className="gedaempft">Bild lädt …</p>
  if (bild.art === 'fehler') return <Bildfehler zustand={bild} />
  return <img className="bild-gross" src={bild.adresse} alt="Gemeldetes Profilbild" />
}
